import * as R from 'remeda';
import type { CascaderOption } from '../helpers/types';
import { getAllDescendantPaths, getSelectedOptions, isPathSelected } from './path-utils';

const joinKey = (path: string[]) => path.join('|');

/**
 * パス同士が完全一致するか
 */
export const isPathEqual = (path1: string[], path2: string[]): boolean =>
  R.isDeepEqual(path1, path2);

/**
 * path が ancestorPath の子孫か
 */
export const isDescendantPath = (path: string[], ancestorPath: string[]): boolean =>
  path.length > ancestorPath.length &&
  R.isDeepEqual(path.slice(0, ancestorPath.length), ancestorPath);

/**
 * 親パスを上位から順に取得
 */
const getAncestorPaths = (path: string[]): string[][] =>
  R.pipe(
    R.range(1, path.length),
    R.map((i) => path.slice(0, i)),
  );

/**
 * path 配列を重複なく統合
 */
const mergeUniquePaths = (base: string[][], additions: string[][]): string[][] =>
  R.uniqueBy([...base, ...additions], joinKey);

/**
 * 対象パスと子孫を削除し、不要な親も整理
 */
export const removeBranch = (paths: string[][], targetPath: string[]): string[][] => {
  let next = paths.filter(
    (path) => !isPathEqual(path, targetPath) && !isDescendantPath(path, targetPath),
  );

  // 子がなくなった親を削除
  for (let i = targetPath.length - 1; i >= 1; i--) {
    const parentPath = targetPath.slice(0, i);
    const hasChildren = next.some(
      (path) => path.length > parentPath.length && isDescendantPath(path, parentPath),
    );
    if (!hasChildren) {
      next = next.filter((path) => !isPathEqual(path, parentPath));
    }
  }

  return next;
};

/**
 * 対象パスと子孫、祖先を追加
 */
export const addBranch = (
  paths: string[][],
  targetPath: string[],
  option: CascaderOption,
): string[][] => {
  const descendants = getAllDescendantPaths(targetPath.slice(0, -1), option);
  const ancestors = getAncestorPaths(targetPath);
  return mergeUniquePaths(paths, [...descendants, ...ancestors]);
};

/**
 * 複数選択のトグル結果を返す
 */
export const toggleBranchSelection = (
  paths: string[][],
  targetPath: string[],
  option: CascaderOption,
): string[][] => {
  const isSelected = isPathSelected(targetPath, paths);
  return isSelected ? removeBranch(paths, targetPath) : addBranch(paths, targetPath, option);
};

/**
 * 最も深いパスを取得
 */
export const getLongestPath = (paths: string[][]): string[] =>
  R.firstBy(paths, [(p) => p.length, 'desc']) ?? [];

/**
 * 選択パスを最小限のセットに統合する
 *
 * すべての子孫が選択されている親ノードは、子孫パスを除外して親パスのみ残す。
 * これにより onChange に渡される値が最小限の表現になる。
 *
 * @example
 * // 全子孫が選択されている場合
 * consolidatePaths(
 *   [["zhejiang"], ["zhejiang","hangzhou"], ["zhejiang","hangzhou","xihu"], ["zhejiang","hangzhou","shangcheng"], ...],
 *   options
 * )
 * // => [["zhejiang"]]
 */
export const consolidatePaths = (
  selectedPaths: string[][],
  options: CascaderOption[],
): string[][] => {
  if (selectedPaths.length === 0) return [];

  const result: string[][] = [];
  const coveredKeys = new Set<string>();

  // パスの長さでソート（短い順 = 親優先）
  const sortedPaths = R.sortBy(selectedPaths, (p) => p.length);

  for (const path of sortedPaths) {
    const key = joinKey(path);
    if (coveredKeys.has(key)) continue;

    const lastOpt = R.last(getSelectedOptions(path, options));
    if (!lastOpt) continue;

    if (lastOpt.children && lastOpt.children.length > 0) {
      // 親ノード: すべての子孫が選択されているかチェック
      const allDescendants = getAllDescendantPaths(path.slice(0, -1), lastOpt);
      const allSelected = allDescendants.every((dp) => isPathSelected(dp, selectedPaths));

      if (allSelected) {
        result.push(path);
        for (const dp of allDescendants) {
          coveredKeys.add(joinKey(dp));
        }
      }
      // 全子孫が選択されていない場合はスキップ（子ノードが自身をハンドル）
    } else {
      // リーフノード
      result.push(path);
      coveredKeys.add(key);
    }
  }

  return result;
};

/**
 * 統合されたパスをフル展開に戻す
 *
 * 統合パス（祖先関係なし）の場合は全子孫+祖先を展開し、
 * 展開済みパス（祖先関係あり）の場合はそのまま返す。
 *
 * @example
 * expandConsolidatedPaths([["zhejiang"]], options)
 * // => [["zhejiang"], ["zhejiang","hangzhou"], ["zhejiang","hangzhou","xihu"], ...]
 */
export const expandConsolidatedPaths = (
  paths: string[][],
  options: CascaderOption[],
): string[][] => {
  if (paths.length === 0) return [];

  // 祖先関係がある場合は展開済み → そのまま返す
  const hasAncestorRelationships = paths.some((p1) =>
    paths.some((p2) => p1 !== p2 && isDescendantPath(p2, p1)),
  );
  if (hasAncestorRelationships) return paths;

  // 統合パスを展開
  let expanded: string[][] = [];
  for (const path of paths) {
    const lastOpt = R.last(getSelectedOptions(path, options));
    if (!lastOpt) continue;
    expanded = addBranch(expanded, path, lastOpt);
  }

  return expanded;
};
